import type { AuditResult, PhasePrompt, Roadmap } from '@/types/plan';

export interface FinalAuditPhaseSummary {
  phaseId: string;
  verdict: 'pass' | 'warn' | 'fail';
  score: number;
  issues: string[];
}

export interface FinalAuditReadiness {
  canRunFinalAudit: boolean;
  blockingIssues: string[];
  phaseAudits: FinalAuditPhaseSummary[];
}

interface FinalAuditCap {
  maxScore: number;
  verdict: 'pass' | 'warn' | 'fail';
  reasons: string[];
}

const DETERMINISTIC_DEGRADATION_PATTERNS = [
  /deterministic/i,
  /verification/i,
  /unresolved (?:execution )?tokens?/i,
  /degraded mode/i,
  /grounding override/i,
  /blocked during execution/i,
];

function dedupeIssues(issues: string[]): string[] {
  return Array.from(new Set(issues.map((issue) => issue.trim()).filter(Boolean)));
}

function summarizeImplementationAuditIssues(audit: AuditResult): string[] {
  const criterionIssues = audit.criteria
    ?.filter((criterion) => criterion.verdict !== 'pass')
    .map((criterion) => `[${criterion.name}] ${criterion.reasoning}`) ?? [];

  return dedupeIssues([
    ...criterionIssues,
    ...(audit.blockingIssues ?? []),
    ...(audit.warnings ?? []),
  ]);
}

export function hasMeaningfulDeterministicDegradation(issues: string[]): boolean {
  return issues.some((issue) =>
    DETERMINISTIC_DEGRADATION_PATTERNS.some((pattern) => pattern.test(issue)),
  );
}

export function buildFinalAuditReadiness(params: {
  phases: PhasePrompt[];
  implementationAudits: Record<string, AuditResult>;
  executionErrors: Record<string, string>;
}): FinalAuditReadiness {
  const { phases, implementationAudits, executionErrors } = params;

  const blockingIssues: string[] = [];
  const phaseAudits = phases.map((phase) => {
    const executionError = executionErrors[phase.id];
    if (executionError) {
      const issue = `Phase ${phase.id} (${phase.title}) was blocked during execution: ${executionError}`;
      blockingIssues.push(issue);
      return {
        phaseId: phase.id,
        verdict: 'fail' as const,
        score: 0,
        issues: [issue],
      };
    }

    const audit = implementationAudits[phase.id];
    if (!audit) {
      const issue = `Phase ${phase.id} (${phase.title}) did not produce an implementation audit result`;
      blockingIssues.push(issue);
      return {
        phaseId: phase.id,
        verdict: 'fail' as const,
        score: 0,
        issues: [issue],
      };
    }

    return {
      phaseId: phase.id,
      verdict: audit.verdict,
      score: audit.overallScore,
      issues: summarizeImplementationAuditIssues(audit),
    };
  });

  return {
    canRunFinalAudit: blockingIssues.length === 0,
    blockingIssues,
    phaseAudits,
  };
}

export function buildSyntheticIncompleteFinalAudit(
  roadmap: Roadmap,
  blockingIssues: string[],
): AuditResult {
  const uniqueIssues = Array.from(new Set(blockingIssues));

  return {
    id: `synthetic-final-${roadmap.id}`,
    auditType: 'final',
    targetId: roadmap.id,
    verdict: 'fail',
    overallScore: 0,
    criteria: [
      {
        name: 'pipeline_completeness',
        verdict: 'fail',
        score: 0,
        reasoning: 'The pipeline did not complete all roadmap phases, so a holistic final audit would be misleading.',
        feedback: [
          {
            text: 'At least one phase was blocked or missing its implementation audit result.',
            sentiment: 'negative',
          },
          {
            text: 'Final audit was synthesized locally to avoid reporting a false overall pass.',
            sentiment: 'neutral',
          },
        ],
        suggestions: [
          'Fix the blocked or missing phase results before rerunning the final audit.',
        ],
      },
    ],
    blockingIssues: uniqueIssues,
    warnings: [],
    suggestions: [
      'Resolve the failed phase and rerun the pipeline to produce a trustworthy holistic audit.',
    ],
    auditedAt: new Date().toISOString(),
    modelUsed: 'synthetic',
    retryCount: 0,
  };
}

function buildFinalAuditCap(phaseAudits: FinalAuditPhaseSummary[]): FinalAuditCap | null {
  const failedPhases = phaseAudits.filter((phase) => phase.verdict === 'fail');
  if (failedPhases.length > 0) {
    return {
      maxScore: 49,
      verdict: 'fail',
      reasons: failedPhases.map((phase) =>
        `Final audit capped because ${phase.phaseId} failed its implementation audit (${phase.score}/100).`,
      ),
    };
  }

  const degradedWarnPhases = phaseAudits.filter((phase) =>
    phase.verdict === 'warn' && hasMeaningfulDeterministicDegradation(phase.issues),
  );
  if (degradedWarnPhases.length > 0) {
    return {
      maxScore: 69,
      verdict: 'warn',
      reasons: degradedWarnPhases.map((phase) =>
        `Final audit capped because ${phase.phaseId} reported deterministic degradation that weakens holistic confidence.`,
      ),
    };
  }

  const warnedPhases = phaseAudits.filter((phase) => phase.verdict === 'warn');
  if (warnedPhases.length > 0) {
    return {
      maxScore: 79,
      verdict: 'warn',
      reasons: warnedPhases.map((phase) =>
        `Final audit capped because ${phase.phaseId} only achieved WARN in its implementation audit (${phase.score}/100).`,
      ),
    };
  }

  return null;
}

export function applyFinalAuditScoreCaps(
  audit: AuditResult,
  phaseAudits: FinalAuditPhaseSummary[],
): AuditResult {
  const cap = buildFinalAuditCap(phaseAudits);
  if (!cap) return audit;

  const nextScore = Math.min(audit.overallScore, cap.maxScore);
  const nextWarnings = dedupeIssues([
    ...(audit.warnings ?? []),
    ...cap.reasons,
  ]);
  const nextSuggestions = dedupeIssues([
    ...(audit.suggestions ?? []),
    'Use the per-phase implementation audits as the source of truth before trusting a high final-audit score.',
  ]);

  return {
    ...audit,
    verdict: cap.verdict,
    overallScore: nextScore,
    warnings: nextWarnings,
    suggestions: nextSuggestions,
  };
}
